import {
  effectAsParsed,
  effectRulesDefaults,
  zoneDefaults,
  zoneDefaultsDefaults,
} from '../effects/defaults';
import {
  BASE_ZONE_KINDS,
  EFFECT_APPLIES_TO,
  EFFECT_DURATION_KINDS,
  EFFECT_POLARITIES,
  EFFECT_SCOPES,
  EFFECT_STACK_MODES,
  EFFECT_TRIGGERS,
  GROUP_SCOPES,
  ZONE_MAX_GRID_SIDE_M,
  ZONE_MAX_RADIUS_M,
  ZONE_MIN_RADIUS_M,
  findModifierKey,
  isStatKey,
  triggerListParams,
  triggerParams,
  type EffectRulesDef,
  type StatusEffectDef,
  type ZoneDef,
} from '../effects/types';
import { STAT_IDS, STAT_MAX, STAT_MIN } from '../hb/types';
import { ACTIVITY_CATEGORIES } from '../skills/types';
import { legacyKeyHint } from './skills';
import { withDefaults } from '../teamsDefaults';
import { isBlank, num, type Err, type Ids } from './common';
import { sectionEntries } from './sections';
import type { Bundle } from './types';

/*
 * The Effects sections (ADR 0045 §2-§4): a port of packages/shared-cs/Runtime/Content/ContentValidator.Effects.cs (and ZoneRules.ShapeProblems)
 * with the same messages in the same order: statusEffects (ids, then each effect), effectRules, zones (ids, then each zone). The server runs it
 * after the story and before the stats. Absent lists are empty and an absent effectRules is its C# defaults; explicit null is missing.
 */

const has = (list: readonly string[], v: string | null | undefined) =>
  v != null && list.includes(v);
const isChance = (v: number) => v >= 0 && v <= 1;
const finite = (v: unknown) => typeof v === 'number' && Number.isFinite(v);

export function validateEffects(b: Bundle, err: Err, ids: Ids) {
  const effects = sectionEntries('statusEffects', b.statusEffects, err);
  const zones = sectionEntries('zones', b.zones, err);
  ids('statusEffects', b.statusEffects ?? [], 'effect');
  const byId = new Map<string, StatusEffectDef>();
  for (const [raw, i] of effects) {
    const e = effectAsParsed(raw);
    if (e.id && !byId.has(e.id)) byId.set(e.id, e);
    statusEffect(e, (path, message) => err('statusEffects', i, path, message));
  }
  if (b.effectRules === null) err('effectRules', undefined, '', "Bundle is missing 'effectRules'.");
  else
    effectRules(
      withDefaults(effectRulesDefaults(), b.effectRules ?? effectRulesDefaults()),
      byId,
      (path, message) => err('effectRules', undefined, path, message),
    );
  ids('zones', b.zones ?? [], 'zone');
  for (const [raw, i] of zones)
    zone(withDefaults(zoneDefaultsForParse(), raw), byId, (path, message) =>
      err('zones', i, path, message),
    );
}

/** ZoneDef's C# initializers (polar and grid default to null, unlike a new zone from the editor). */
const zoneDefaultsForParse = (): ZoneDef => ({ ...zoneDefaults(), polar: null, staleMinutes: 0 });

type Fail = (path: string, message: string) => void;

function statusEffect(e: StatusEffectDef, fail: Fail) {
  const w = `Effect '${e.id ?? ''}'`;
  if (isBlank(e.name)) fail('name', `${w} needs a name.`);
  if (isBlank(e.family)) fail('family', `${w} needs a family.`);
  if (!has(EFFECT_POLARITIES, e.polarity))
    fail('polarity', `${w} has unknown polarity '${e.polarity ?? ''}' (buff or debuff).`);
  const scopeKnown = has(EFFECT_SCOPES, e.scope);
  if (!scopeKnown)
    fail(
      'scope',
      `${w} has unknown scope '${e.scope ?? ''}' (player, survivor, team, crew or base).`,
    );
  if (has(GROUP_SCOPES, e.scope)) {
    if (!has(EFFECT_APPLIES_TO, e.appliesTo))
      fail(
        'appliesTo',
        `${w} has unknown appliesTo '${e.appliesTo ?? ''}' (everyone, recruits, at_base or player).`,
      );
  } else if (e.appliesTo && e.appliesTo !== 'everyone') {
    fail('appliesTo', `${w} sets appliesTo, which is only for crew and team scopes.`);
  }

  // Powered Up POC (CONTRACT §11): '' or 'copies'; copies only on player-scope effects.
  const mirror = e.mirror ?? '';
  if (mirror !== '' && mirror !== 'copies')
    fail('mirror', `${w} has unknown mirror '${mirror}' (empty or copies).`);
  else if (mirror === 'copies' && e.scope !== 'player')
    fail('mirror', `${w} sets mirror 'copies', which is only for scope 'player'.`);

  const kind = e.duration?.kind ?? '';
  const derived = kind === 'derived';
  if (e.modifiers == null) {
    fail('modifiers', `${w} is missing 'modifiers'.`);
  } else {
    if (e.modifiers.length === 0 && !derived)
      fail('modifiers', `${w} needs at least one modifier.`);
    e.modifiers.forEach((raw, j) => {
      const at = (k: string) => `modifiers.${j}.${k}`;
      if (raw == null) {
        fail(`modifiers.${j}`, `${w} has an empty modifier.`);
        return;
      }
      const m = withDefaults({ key: '', op: 'add', value: 0 }, raw);
      const key = findModifierKey(m.key);
      if (key == null) {
        fail(at('key'), `${w} uses unknown modifier key '${m.key ?? ''}'${legacyKeyHint(m.key)}.`);
        return;
      }
      if (m.op !== key.op) fail(at('op'), `${w} modifier '${m.key}' needs op '${key.op}'.`);
      if (scopeKnown && !key.scopes.includes(e.scope!))
        fail(at('key'), `${w} modifier '${m.key}' is not allowed for scope '${e.scope}'.`);
      if (!finite(m.value)) fail(at('value'), `${w} modifier '${m.key}' needs a finite value.`);
      else if (key.op === 'mult' && !(num(m.value) > 0))
        fail(at('value'), `${w} modifier '${m.key}' needs a value above 0.`);
    });
  }

  if (e.duration == null) fail('duration', `${w} is missing 'duration'.`);
  else if (!has(EFFECT_DURATION_KINDS, kind))
    fail(
      'duration.kind',
      `${w} has unknown duration kind '${kind}' (timed, zone, until_cleared or derived).`,
    );
  else if (kind === 'timed' && !(num(e.duration.hours) > 0))
    fail('duration.hours', `${w} needs duration.hours above 0.`);

  const s = e.stacking;
  if (s == null) {
    fail('stacking', `${w} is missing 'stacking'.`);
  } else if (!has(EFFECT_STACK_MODES, s.onRepeat)) {
    fail(
      'stacking.onRepeat',
      `${w} has unknown stacking.onRepeat '${s.onRepeat ?? ''}' (refresh, extend, stack or ignore).`,
    );
  } else if (s.onRepeat === 'extend') {
    if (kind !== 'timed') fail('stacking.onRepeat', `${w} can only extend a timed duration.`);
    if (!(num(s.addH) > 0)) fail('stacking.addH', `${w} needs stacking.addH above 0.`);
    if (num(s.maxH) < num(s.addH)) fail('stacking.maxH', `${w} needs stacking.maxH >= addH.`);
  } else if (s.onRepeat === 'stack') {
    if (num(s.max) < 2) fail('stacking.max', `${w} needs stacking.max >= 2.`);
    if (s.values == null || s.values.length !== num(s.max))
      fail('stacking.values', `${w} needs one stacking.values entry per stack (${num(s.max)}).`);
    else if (s.values.some((v) => !finite(v) || !(v > 0)))
      fail('stacking.values', `${w} needs stacking.values above 0.`);
  }
  const stackCount = s != null && s.onRepeat === 'stack' ? Math.max(1, num(s.max)) : 1;
  if (e.text?.stackNames != null && e.text.stackNames.length > stackCount)
    fail('text.stackNames', `${w} has more text.stackNames than stacks.`);

  const r = e.resist;
  if (r != null) {
    if (e.polarity !== 'debuff') fail('resist', `${w} has a resist, which is only for debuffs.`);
    if (!has(STAT_IDS, r.stat))
      fail('resist.stat', `${w} resist uses unknown stat '${r.stat ?? ''}'.`);
    if (r.ignoreAt == null) fail('resist.ignoreAt', `${w} resist is missing 'ignoreAt'.`);
    else {
      let last = 0;
      for (const at of r.ignoreAt) {
        if (at < STAT_MIN || at > STAT_MAX) {
          fail('resist.ignoreAt', `${w} resist needs ignoreAt values in ${STAT_MIN}..${STAT_MAX}.`);
          break;
        }
        if (at <= last) {
          fail('resist.ignoreAt', `${w} resist needs ignoreAt ascending.`);
          break;
        }
        last = at;
      }
    }
    if (!isChance(num(r.shortenPerPt)))
      fail('resist.shortenPerPt', `${w} resist needs shortenPerPt in 0..1.`);
    const md = num(r.minDurationMult);
    if (!(md > 0) || md > 1)
      fail('resist.minDurationMult', `${w} resist needs minDurationMult in (0..1].`);
    const above = num(r.shortenAbove);
    if (above < 0 || above > STAT_MAX)
      fail('resist.shortenAbove', `${w} resist needs shortenAbove in 0..${STAT_MAX}.`);
  }

  if (e.triggers == null) {
    fail('triggers', `${w} is missing 'triggers'.`);
  } else {
    if (e.triggers.length > 0 && (kind === 'zone' || derived))
      fail('triggers', `${w} has triggers, but a ${kind} effect is never triggered.`);
    e.triggers.forEach((t, j) => {
      if (t == null) {
        fail(`triggers.${j}`, `${w} has an empty trigger.`);
        return;
      }
      const event = t.event ?? '';
      if (!has(EFFECT_TRIGGERS, event)) {
        fail(`triggers.${j}.event`, `${w} trigger uses unknown event '${event}'.`);
        return;
      }
      if (t.listParams != null) {
        const lists = triggerListParams(event);
        for (const [k, v] of Object.entries(t.listParams)) {
          if (!lists.includes(k))
            fail(`triggers.${j}.listParams`, `${w} trigger '${event}' has unknown param '${k}'.`);
          else if (
            !Array.isArray(v) ||
            v.length === 0 ||
            v.some((c) => !(ACTIVITY_CATEGORIES as readonly string[]).includes(c))
          )
            fail(
              `triggers.${j}.listParams`,
              `${w} trigger '${event}' ${k} must list known categories.`,
            );
        }
      }
      if (t.params == null) return;
      const allowed = triggerParams(event);
      for (const [k, v] of Object.entries(t.params)) {
        if (!allowed.includes(k))
          fail(`triggers.${j}.params`, `${w} trigger '${event}' has unknown param '${k}'.`);
        else if (typeof v !== 'number' || !finite(v) || v < 0)
          fail(`triggers.${j}.params`, `${w} trigger '${event}' needs ${k} >= 0.`);
      }
    });
  }
}

/** shared-cs `ZoneRules.DistanceM` (MotionRules' haversine, R = 6371008.8 m). */
export function distanceM(lat1: number, lng1: number, lat2: number, lng2: number) {
  const R = 6371008.8;
  const rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad;
  const dLng = (lng2 - lng1) * rad;
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

const latOk = (v: number) => !Number.isNaN(v) && v >= -90 && v <= 90;
const lngOk = (v: number) => !Number.isNaN(v) && v >= -180 && v <= 180;

/** shared-cs `ZoneRules.ShapeProblems`: each message follows "Zone 'id' ". */
export function shapeProblems(z: ZoneDef): string[] {
  const out: string[] = [];
  if (z.shape === 'polar') {
    const p = z.polar;
    if (p == null) return ['needs polar {lat, lng, radiusM}.'];
    if (!latOk(num(p.lat))) out.push('needs polar.lat in -90..90.');
    if (!lngOk(num(p.lng))) out.push('needs polar.lng in -180..180.');
    const r = num(p.radiusM);
    if (r !== 0 && (Number.isNaN(r) || r < ZONE_MIN_RADIUS_M || r > ZONE_MAX_RADIUS_M))
      out.push(
        `needs polar.radiusM 0 (the default) or in ${ZONE_MIN_RADIUS_M}..${ZONE_MAX_RADIUS_M}.`,
      );
  } else if (z.shape === 'grid') {
    const g = z.grid;
    if (g == null) return ['needs grid {minLat, minLng, maxLat, maxLng}.'];
    const [minLat, minLng, maxLat, maxLng] = [
      num(g.minLat),
      num(g.minLng),
      num(g.maxLat),
      num(g.maxLng),
    ];
    if (!latOk(minLat) || !latOk(maxLat)) out.push('needs grid latitudes in -90..90.');
    if (!lngOk(minLng) || !lngOk(maxLng)) out.push('needs grid longitudes in -180..180.');
    if (!(minLat < maxLat) || !(minLng < maxLng)) {
      out.push('needs grid min below max on both axes.');
      return out;
    }
    const mid = (minLat + maxLat) / 2;
    const ns = distanceM(minLat, minLng, maxLat, minLng);
    const ew = distanceM(mid, minLng, mid, maxLng);
    if (ns > ZONE_MAX_GRID_SIDE_M || ew > ZONE_MAX_GRID_SIDE_M)
      out.push(`has a side over ${ZONE_MAX_GRID_SIDE_M} m.`);
  } else {
    out.push(`has unknown shape '${z.shape ?? ''}' (polar or grid).`);
  }
  return out;
}

function zone(z: ZoneDef, effects: Map<string, StatusEffectDef>, fail: Fail) {
  const w = `Zone '${z.id ?? ''}'`;
  if (isBlank(z.kind)) fail('kind', `${w} needs a kind.`);
  else if (has(BASE_ZONE_KINDS, z.kind))
    fail('kind', `${w} uses kind '${z.kind}', which is reserved for base zones.`);
  for (const p of shapeProblems(z)) fail(z.shape === 'grid' ? 'grid' : 'polar', `${w} ${p}`);
  if (num(z.staleMinutes) < 0) fail('staleMinutes', `${w} staleMinutes is negative.`);
  zoneEffects(z.effects, w, effects, (m) => fail('effects', m));
}

function zoneEffects(
  ids: (string | null)[] | null | undefined,
  w: string,
  effects: Map<string, StatusEffectDef>,
  fail: (message: string) => void,
) {
  if (ids == null || ids.length === 0) {
    fail(`${w} needs at least one effect.`);
    return;
  }
  for (const id of ids) {
    const e = id == null ? undefined : effects.get(id);
    if (e === undefined) fail(`${w} grants unknown effect '${id ?? ''}'.`);
    else if (e.duration?.kind !== 'zone')
      fail(`${w} grants effect '${id}', whose duration kind isn't zone.`);
  }
}

function effectRules(r: EffectRulesDef, effects: Map<string, StatusEffectDef>, fail: Fail) {
  const where = 'Effect rules';
  if (num(r.maxActive) < 1) fail('maxActive', `${where} maxActive must be at least 1.`);
  if (num(r.statCap) < 0) fail('statCap', `${where} statCap is negative.`);
  for (const [k, c] of Object.entries(r.keys ?? {})) {
    const key = findModifierKey(k);
    const w = `${where} key '${k}'`;
    const path = `keys.${k}`;
    if (key == null) {
      fail(path, `${w} is not a registered modifier key.`);
      continue;
    }
    if (c == null) {
      fail(path, `${w} has no clamps.`);
      continue;
    }
    const { sumMin, sumMax, finalMin, finalMax } = c;
    if (isStatKey(k) && (sumMin != null || sumMax != null))
      fail(path, `${w} sets a sum clamp; stats use effectRules.statCap.`);
    if (sumMin != null && sumMax != null && sumMin > sumMax)
      fail(path, `${w} needs sumMin <= sumMax.`);
    if (finalMin != null && finalMax != null && finalMin > finalMax)
      fail(path, `${w} needs finalMin <= finalMax.`);
    if (
      key.op === 'mult' &&
      ((sumMin != null && !(sumMin > 0)) || (finalMin != null && !(finalMin > 0)))
    )
      fail(path, `${w} needs clamps above 0 on a mult key.`);
  }

  if (r.zoneDefaults == null) {
    fail('zoneDefaults', `${where} are missing 'zoneDefaults'.`);
  } else {
    const z = withDefaults(zoneDefaultsDefaults(), r.zoneDefaults);
    const radius = num(z.radiusM);
    if (radius < ZONE_MIN_RADIUS_M || radius > ZONE_MAX_RADIUS_M)
      fail(
        'zoneDefaults.radiusM',
        `${where} need zoneDefaults.radiusM in ${ZONE_MIN_RADIUS_M}..${ZONE_MAX_RADIUS_M}.`,
      );
    if (z.homeRadiusM != null && !(z.homeRadiusM > 0))
      fail('zoneDefaults.homeRadiusM', `${where} need zoneDefaults.homeRadiusM null or above 0.`);
    if (z.campRadiusM != null && !(z.campRadiusM > 0))
      fail('zoneDefaults.campRadiusM', `${where} need zoneDefaults.campRadiusM null or above 0.`);
    for (const k of ['staleMinutes', 'homeStaleMinutes', 'campStaleMinutes'] as const)
      if (num(z[k]) < 1)
        fail(`zoneDefaults.${k}`, `${where} zoneDefaults.${k} must be at least 1.`);
  }

  const bz = r.baseZoneEffects;
  if (bz == null) {
    fail('baseZoneEffects', `${where} are missing 'baseZoneEffects'.`);
    return;
  }
  for (const k of ['home', 'camp'] as const) {
    const list = bz[k];
    if (list == null || list.length === 0) continue; // no bonus at this kind of base is fine
    zoneEffects(list, `${where} baseZoneEffects.${k}`, effects, (m) =>
      fail(`baseZoneEffects.${k}`, m),
    );
  }
}
